import React, { useMemo, useEffect, useState } from 'react';

import { UploadStatus, VideoShowStatus, VideoFailedReason } from '@/types/components/Video';
import { inject, observer } from 'mobx-react';
import { AgentStore, UploadFileStatus } from '@/stores/agent';
import { addExtraParamsForPlayVideo, addFileProtocol, sendVideoMessage, trackVideoFinish, handleVideoUploadFinishReport } from '@/utils/videoTools';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { replaceHttpLinkWithHttps } from '@/utils/tools';

import VideoControl from './VideoControl';
import VideoStatus from './VideoStatus';
import clsx from 'classnames';

import './style.scss';
import reporter from '@/utils/report';
import defaultImg from '../../assets/img/default_img.svg';

export interface VideoProps {
  /** 缩略图 */
  thumbnail: string;
  /** bridge 给的videoId */
  vid: string;
  /** 视频时间 ms */
  videoDuration: number;
  /** video 上传路径 */
  videoUrl: string;
  /** video 本地路径 */
  videoFilePath: string;
  /** video 封面url */
  videoCoverUrl: string;
  /** 当前状态 */
  status: UploadStatus;
  /** 上传耗时 */
  uploadCostTime?: number;
  messageId?: string;
  /** 后端返回错误码，当前只能拿到message接口返回的错误 */
  beErrorCode?: number;
  curPos?: number;
  width?: number;
  height?: number;
  mmsId: string;

  /** bot | user | agent */
  from: string;
  isHistory: boolean;

  agentStore: AgentStore;
  userStore: any;
}

const PREFIX = 'video-wrapper';

function Video(props: VideoProps) {
  // 加载视频封面错误
  const [getThumbnailError, setGetThumbnailError] = useState(false);
  // 用于展示封面图
  // 从 floating bubble 返回时 展示cdn url
  const [thumbnailToShow, setThumbnailToShow] = useState<string>();

  // 用于给后端发送消息的参数
  const sendMessageParams = {
    text: props.videoUrl!,
    type: 'VID',
    params: {
      vid: props.mmsId,
      width: props.width,
      height: props.height,
      url: replaceHttpLinkWithHttps(props.videoUrl!),
      thumbnail: replaceHttpLinkWithHttps(props.videoCoverUrl!),
      duration: props.videoDuration,
    },
  };

  // 图片加载错误
  const handleThumbnailError = () => {
    setGetThumbnailError(true);
    reporter.apiBatchReport({ type: 'count', value: { label: 'cs_video_cover_load_error' }});
    reporter.apiLog('cs_video_cover_load_error', { level: 'error', url: props.videoCoverUrl });
  };

  const videoShowStatus = useMemo(() => {
    // 压缩中
    // 展示 loading
    const showVideoUploading = [
      UploadStatus.Compressing,
    ].includes(props.status);
    // 压缩成功 or 上传中 or 上传成功
    // 展示播放按钮
    const showVideoPlayButton = [
      UploadStatus.CompressSucceed,
      UploadStatus.Uploading,
      UploadStatus.UploadSucceed,
      UploadStatus.UploadFailed,
      UploadStatus.SendingMessage,
      UploadStatus.SendMessageSucceed,
      UploadStatus.SendMessageFailed,
    ].includes(props.status);
    // 压缩失败
    // 展示 failed
    const showVideoFailed = [
      UploadStatus.CompressFailed,
      UploadStatus.GetTokenFailed,
      UploadStatus.SessionEnd,
    ].includes(props.status);

    // 对于cancel状态 作特殊处理
    // 因为 可以从任何状态 变为 cancel 状态
    if (props.status === UploadStatus.UploadCanceled) {
      if (props.videoUrl || props.videoFilePath) {
        return VideoShowStatus.Play;
      } else {
        return VideoShowStatus.Failed;
      }
    }

    if (showVideoUploading) {
      return VideoShowStatus.Uploading;
    } else if (showVideoPlayButton) {
      return VideoShowStatus.Play;
    } else if (showVideoFailed) {
      return VideoShowStatus.Failed;
    }
  }, [props.status]);

  // 发送消息成功
  const finish = useMemo(() => [
    UploadStatus.SendMessageSucceed,
  ].includes(props.status), [props.status]);

  // 压缩错误 or 上传错误
  const error = useMemo(() => [
    UploadStatus.UploadCanceled,
    UploadStatus.CompressFailed,
    UploadStatus.UploadFailed,
    UploadStatus.GetTokenFailed,
    UploadStatus.SendMessageFailed,
    UploadStatus.SessionEnd,
  ].includes(props.status), [props.status]);

  useEffect(() => {
    // 上报至grafana
    handleVideoUploadFinishReport(props.status);

    // 上传成功 发送消息
    if (props.status === UploadStatus.UploadSucceed) {
      sendVideoMessage(props.vid, sendMessageParams);
    }

    // 上传的结束状态
    // 发送消息失败单独处理
    if ([
      UploadStatus.GetTokenFailed,
      UploadStatus.CompressFailed,
      UploadStatus.UploadSucceed,
      UploadStatus.UploadFailed,
      UploadStatus.UploadCanceled,
    ].includes(props.status)) {
      props.agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADED);
    }

    // 上报
    if ([
      UploadStatus.CompressFailed,
      UploadStatus.UploadFailed,
      UploadStatus.GetTokenFailed,
      UploadStatus.SessionEnd,
    ].includes(props.status)) {
      // @ts-ignore
      const failureReason = VideoFailedReason[props.status];

      trackVideoFinish({
        isUploadSuccess: !!props.videoUrl,
        isSendSuccess: false,
        duration: props.videoDuration,
        uploadDuration: props.uploadCostTime,
        failureReason: failureReason,
      });
    }
  }, [props.status]);

  useEffect(() => {
    // 从 floating bubble 返回时 展示cdn url
    setThumbnailToShow(props.videoCoverUrl || props.thumbnail);
  }, []);

  return (
    <div className={clsx(PREFIX, { 'history-video': props.isHistory })}>
      <div className="video-content">
        <div className="thumbnail-wrapper">
          {
            getThumbnailError ? (
              <img
                className="video-thumbnail"
                src={defaultImg}
                onError={handleThumbnailError}
              />
            ) : (
              <img
                className="video-thumbnail"
                src={replaceHttpLinkWithHttps(thumbnailToShow)}
                onError={handleThumbnailError}
              />
            )
          }
        </div>

        <VideoControl
          getThumbnailError={getThumbnailError}
          isHistory={props.isHistory}
          status={videoShowStatus!}
          duration={props.videoDuration}
          messageId={props.messageId}
          loyaltyTierCode={props.userStore.loyaltyTierCode}
          videoPath={props.videoUrl ? replaceHttpLinkWithHttps(addExtraParamsForPlayVideo(props.videoUrl)) : addFileProtocol(props.videoFilePath)}
        />
      </div>

      <VideoStatus
        curPos={props.curPos}
        reverse={props.from !== 'user'}
        vid={props.vid}
        error={error}
        finish={finish}
        status={props.status}
        duration={props.videoDuration}
        sendMessageParams={sendMessageParams}
        beErrorCode={props.beErrorCode}
      />
    </div>
  );
}

export default inject(getChildInject('agentStore', 'userStore'))(observer(Video));
